import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SnackbarService } from '@fitscovery/ui/snackbar';
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ArchiveMember, CreateMember, CreateMembershipSubscription, CreateMembershipSubscriptionWithPMSEmailTemplate, DeleteMembershipSubscription, GetEmailTemplatesByPartnerId, GetMembersByPartnerId, GetMembershipSubscriptionsByPartnerId, GetMembershipWithHistory, ImportMembers, SetMembershipSubscriptionOrder, StopMembershipRecurring, UpdateMember, UpdateMembershipSubscription, UpdateMembershipSubscriptionWithPMSEmailTemplate, GetMembershipSubscriptionById, GetEventActiveMembers, RemoveSelectedValidActiveMembers } from '../actions';
import { GetEventActiveMembersResponse, MemberCreateResponse, Membership, MembershipStateModel, MembershipSubscription, MembershipSubscriptionResponse, MembershipSubscriptionWithPMSEmailTemplateCreateResponse, MembershipWithHistory } from '../models';
import { ApiMembershipService } from '../services';
import { MembershipsControlState } from './memberships-control-state';

export const membershipStateDefaults: MembershipStateModel = {
	data: null,
  response: null,
  subscription: null,
  subscriptions: null,
  selectedValidActiveMembers: null,
  history: null,
	loaded: false
};

@State<MembershipStateModel>({
	name: 'membership',
	defaults: membershipStateDefaults
})

@Injectable()
export class MembershipState extends MembershipsControlState implements NgxsOnInit {

	@Selector()
	static memberships(state: MembershipStateModel): Membership[] | null {
		return state.data;
	}

	@Selector()
	static subscriptions(state: MembershipStateModel): MembershipSubscription[] | null {
		return state.subscriptions;
	}

	@Selector()
	static history(state: MembershipStateModel): MembershipWithHistory[] | null {
		return state.history;
	}

	constructor(
    private store: Store,
    private membershipService: ApiMembershipService,
    private snackbarService: SnackbarService
  ) {
    super(snackbarService);
  }

	ngxsOnInit(): void {
	}

	@Action(ImportMembers)
	importMembers(ctx: StateContext<MembershipStateModel>, action: ImportMembers) {
    this.membershipService.importMembers(action.formData).subscribe((response: any) => {

      const userAndMembershipResponses = response.userAndMembershipResponses;
      const hasInvalidEmail = userAndMembershipResponses.errors.filter((e: any) => e.includes('INVALID_EMAIL')).length > 0;
      
      if (hasInvalidEmail)
        return this.snackbarService.openSnack({ message: 'Invalid email(s) found. Please try again.' })

      if (!userAndMembershipResponses.data) {

        this.snackbarService.openSnack({ message: userAndMembershipResponses.errors[0] });
      } else {

        this.snackbarService.openSnack({ message: `New members added!` });
        const member = <CreateMember>{member: { partnerId: response.request.partnerId }}
        this.getMembership(member);

        const message = userAndMembershipResponses.data;
        const numbers = message.replace('and', ',').split(',').map((e: any) => e.trim().replace(/\D/g, ''));
        const usersFound = numbers[0];
        const emailAlreadyExists = numbers[2];
  
        const foundMessage = `${usersFound} members found.`
        const existMessage = `${emailAlreadyExists} emails already exist.`

        this.snackbarService.openSnack({ message: `New members added!` }).afterDismissed().pipe(
          switchMap(() => this.snackbarService.openSnack({ message: foundMessage, duration: 1700 }).afterDismissed()),
          switchMap(() => this.snackbarService.openSnack({ message: existMessage, duration: 1700 }).afterDismissed())
        ).subscribe();
      }
    });
	}

	@Action(CreateMember)
	createMember(ctx: StateContext<MembershipStateModel>, action: CreateMember) {
    this.membershipService.createMember(action.member).pipe(
      catchError((response: HttpErrorResponse) => {
        this.snackbarService.openSnack({ message: response.error.errors[0] });
        return EMPTY;
      })
    ).subscribe((response: MemberCreateResponse) => {
      // TODO: workaround logic only, due to no patch being invoked bug.
      const data = ctx.getState().data!.slice(0);
      ctx.patchState({ data, response: response.request, loaded: true });
      this.getMembership(action);
      this.snackbarService.openSnack({ message: `New member created!` });
    });
	}

	@Action(ArchiveMember)
	archiveMember(ctx: StateContext<MembershipStateModel>, action: ArchiveMember) {
    this.membershipService.archiveMember(action.member).subscribe((member: Membership) => {
      // TODO: workaround logic only, due to no patch being invoked bug.
      const members = ctx.getState().data!.slice(0);
      const index = members.findIndex(e => e.id === member.id);
      const memberInfo = members[index].memberInfo;
      member.memberInfo = memberInfo;
      members[index] = member;
      ctx.patchState({ data: members, loaded: true });
      this.getMembership(action);
      const selectedMember = action.member.memberInfo;
      this.snackbarService.openSnack({ message: `Successfully Archived member ${selectedMember.email}!` });
    });
	}

	@Action(UpdateMember)
	updateMember(ctx: StateContext<MembershipStateModel>, action: UpdateMember) {
    this.membershipService.updateMember(action.member).subscribe((member: Membership) => {
      // TODO: workaround logic only, due to no patch being invoked bug.
      const members = ctx.getState().data!.slice(0);
      const index = members.findIndex(e => e.id === member.id);
      members[index].status = member.status;
      ctx.patchState({ data: members, loaded: true });
      this.getMembership(action);
      this.snackbarService.openSnack({ message: `Successfully updated!` });
    });
	}

	@Action(StopMembershipRecurring)
	stopMembershipRecurring(ctx: StateContext<MembershipStateModel>, action: StopMembershipRecurring): Observable<Membership> {
    return this.membershipService.stopMembershipRecurring(action.membership).pipe(
      map((membership) => {
        const memberships = ctx.getState().data!.slice(0);
        const index = memberships.findIndex(e => e.id === membership.id);
        if (index > -1) {
          memberships[index].isRecurring = membership.isRecurring;
        }
        ctx.patchState({ data: memberships, loaded: true });
        this.getMembership(new UpdateMember(membership));
        return membership;
      })
    )
	}

	@Action(GetMembersByPartnerId)
	getMembersByPartnerId(ctx: StateContext<MembershipStateModel>, action: GetMembersByPartnerId) {
    return this.membershipService.getMembersByPartnerId(action.partnerId).pipe(
      tap((members: Membership[]) => ctx.patchState({ data: members, loaded: true })),
      this.catchResponse
    );
	}

	@Action(GetMembershipWithHistory)
	getMembershipWithHistory(ctx: StateContext<MembershipStateModel>, action: GetMembershipWithHistory): Observable<MembershipWithHistory[]> {
    return this.membershipService.getMembershipWithHistory(action.membershipId, action.partnerId)
      .pipe(
        catchError((httpErrorResponse: HttpErrorResponse) => {
          let message = 'Failed to get membership!';

          if (httpErrorResponse.error && httpErrorResponse.error.errors && httpErrorResponse.error.errors.length > 0) {
            message = httpErrorResponse.error.errors[0];
          } else if (httpErrorResponse.message) {
            message = httpErrorResponse.message;
          };
          
          this.snackbarService.openSnack({ message });

          throw new Error(message);
        }),
        tap((history: MembershipWithHistory[]) => ctx.patchState({ history: history.slice(0) }))
      )
	}

	@Action(CreateMembershipSubscription)
	createMembershipSubscription(ctx: StateContext<MembershipStateModel>, action: CreateMembershipSubscription) {
    this.membershipService.createMembershipSubscription(action.membershipSubscription).subscribe((response: MembershipSubscriptionResponse) => {
      // TODO: workaround logic only, due to no patch being invoked bug.
      const subscriptions = ctx.getState().subscriptions!.slice(0);
      subscriptions.push(response.request);
      ctx.patchState({ ...ctx.getState(), subscriptions });
      this.getMembershipSubscription(action);
      this.snackbarService.openSnack({ message: `New subscription created!` });
    });
	}

	@Action(UpdateMembershipSubscription)
	updateMembershipSubscription(ctx: StateContext<MembershipStateModel>, action: UpdateMembershipSubscription) {
    this.membershipService.updateMembershipSubscription(action.membershipSubscription).subscribe((subscription: MembershipSubscription) => {
      const subscriptions = ctx.getState().subscriptions!.slice(0);
      const index = subscriptions.findIndex(e => e.id === subscription.id);
      subscriptions[index] = action.membershipSubscription;
      ctx.patchState({ ...ctx.getState(), subscriptions });
      this.getMembershipSubscription(action);
      this.snackbarService.openSnack({ message: `Successfully updated!` });
    });
	}

	@Action(DeleteMembershipSubscription)
	deleteMembershipSubscription(ctx: StateContext<MembershipStateModel>, action: DeleteMembershipSubscription) {
    this.membershipService.deleteMembershipSubscription(action.membershipSubscriptionId).subscribe(() => {
      this.store.dispatch(new GetMembershipSubscriptionsByPartnerId(action.partnerId));
      this.snackbarService.openSnack({ message: `Successfully deleted!` });
    });
	}

  @Action(GetMembershipSubscriptionById)
	getMembershipSubscriptionById(ctx: StateContext<MembershipStateModel>, action: GetMembershipSubscriptionById) {
    return this.membershipService.getMembershipSubscriptionById(action.id).pipe(
      tap((response: MembershipSubscriptionResponse) => ctx.patchState({ subscription: response.data }))
    );
	}

  @Action(GetMembershipSubscriptionsByPartnerId)
	getMembershipSubscriptionsByPartnerId(ctx: StateContext<MembershipStateModel>, action: GetMembershipSubscriptionsByPartnerId) {
    this.membershipService.getMembershipSubscriptionsByPartnerId(action.partnerId).subscribe(
      (subscriptions: MembershipSubscription[]) => ctx.patchState({ ...ctx.getState(), subscriptions })
    );
	}

  @Action(SetMembershipSubscriptionOrder)
	setMembershipSubscriptionOrder(ctx: StateContext<MembershipStateModel>, action: SetMembershipSubscriptionOrder) {
    this.membershipService.setMembershipSubscriptionOrder(action.id, action.partnerId, action.orderNumber).subscribe();
	}

  @Action(CreateMembershipSubscriptionWithPMSEmailTemplate)
	createMembershipSubscriptionWithPMSEmailTemplate(ctx: StateContext<MembershipStateModel>, action: CreateMembershipSubscriptionWithPMSEmailTemplate) {
    this.membershipService.createMembershipSubscriptionWithPMSEmailTemplate(action.membershipSubscriptionWithPMSEmailTemplate).subscribe((response: MembershipSubscriptionWithPMSEmailTemplateCreateResponse) => {
      // TODO: workaround logic only, due to no patch being invoked bug.
      const subscriptions = ctx.getState().subscriptions!.slice(0);
      subscriptions.push(response.request);
      ctx.patchState({ ...ctx.getState(), subscriptions });

      this.getMembershipSubscription2(action);
      if(action.membershipSubscriptionWithPMSEmailTemplate.useCustomEmail)
      {
        
        this.getEmailTemplates(action);
    
      }

      this.snackbarService.openSnack({ message: `New subscription created!` });
    });
	}

	@Action(UpdateMembershipSubscriptionWithPMSEmailTemplate)
	updateMembershipSubscriptionWithPMSEmailTemplate(ctx: StateContext<MembershipStateModel>, action: UpdateMembershipSubscriptionWithPMSEmailTemplate) {
    this.membershipService.updateMembershipSubscriptionWithPMSEmailTemplate(action.membershipSubscriptionWithPMSEmailTemplate).subscribe((subscription: MembershipSubscription) => {
      const subscriptions = ctx.getState().subscriptions!.slice(0);
      const index = subscriptions.findIndex(e => e.id === subscription.id);
      subscriptions[index] = action.membershipSubscriptionWithPMSEmailTemplate;
      ctx.patchState({ ...ctx.getState(), subscriptions });
      this.getMembershipSubscription2(action);
      if(action.membershipSubscriptionWithPMSEmailTemplate.useCustomEmail)
      {
        
        this.getEmailTemplates(action);
    
      }
      this.snackbarService.openSnack({ message: `Successfully updated!` });
    });
	}

  @Action(GetEventActiveMembers)
	getEventActiveMembers(ctx: StateContext<MembershipStateModel>, action: GetEventActiveMembers) {
    return this.membershipService.getEventActiveMembers(action.partnerId, action.start, action.noOfTicket).pipe(
      tap((response: GetEventActiveMembersResponse) => ctx.patchState({ selectedValidActiveMembers: response.data })),
      this.catchResponse
    );
	}

  @Action(RemoveSelectedValidActiveMembers)
  removeSelectedValidActiveMembers(ctx: StateContext<MembershipStateModel>) {
    ctx.patchState({ selectedValidActiveMembers: null });
  }

  private getMembershipSubscription2(action: CreateMembershipSubscriptionWithPMSEmailTemplate|UpdateMembershipSubscriptionWithPMSEmailTemplate) {
    const partnerId = action.membershipSubscriptionWithPMSEmailTemplate.partnerId!;
    this.store.dispatch(new GetMembershipSubscriptionsByPartnerId(partnerId));
  }

  private getEmailTemplates(action: CreateMembershipSubscriptionWithPMSEmailTemplate|UpdateMembershipSubscriptionWithPMSEmailTemplate) {
    const partnerId = action.membershipSubscriptionWithPMSEmailTemplate.partnerId!;
    this.store.dispatch(new GetEmailTemplatesByPartnerId(partnerId));
  }

  private getMembership(action: CreateMember|ArchiveMember|UpdateMember) {
    const partnerId = action.member.partnerId;
    this.store.dispatch(new GetMembersByPartnerId(partnerId));
  }

  private getMembershipSubscription(action: CreateMembershipSubscription|UpdateMembershipSubscription) {
    const partnerId = action.membershipSubscription.partnerId!;
    this.store.dispatch(new GetMembershipSubscriptionsByPartnerId(partnerId));
  }

}
